import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { GraduationCap, Plus, Trash, Copy, Check, ArrowSquareOut, UsersThree, IdentificationCard, ArrowsClockwise, ListChecks, X, Flag, ArrowsLeftRight, HandArrowDown, Certificate, Clock, UserSwitch, CheckCircle, ArrowCounterClockwise, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const GRADES = ["G7", "G8", "G9", "G10", "G11", "G12", "DEMO"];
const ADMIN_ROLES = ["super_admin", "school_board_admin", "school_admin"];

const loginUrl = (token) => `${window.location.origin}/impersonate#token=${encodeURIComponent(token)}`;

export function RenameStudentModal({ classId, student, onClose, onSaved }) {
  const guessFirst = student.first_name || (student.name || "").split(" ")[0] || "";
  const guessLast = student.last_name ?? (student.name || "").split(" ").slice(1).join(" ");
  const [first, setFirst] = useState(guessFirst);
  const [last, setLast] = useState(guessLast || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.patch(`/classes/${classId}/students/${student.id}`, { first_name: first.trim(), last_name: last.trim() || null });
      toast.success("Student name updated");
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="rename-student-modal" onClick={onClose}>
      <form onSubmit={save} className="bg-white rounded-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <div className="font-bold font-display mb-1">Edit student name</div>
        <p className="text-xs text-[#000000] mb-3">They'll still log in with their first name + ID.</p>
        <input data-testid="rename-first-input" className="input-base w-full mb-2" placeholder="First name" required value={first} onChange={(e) => setFirst(e.target.value)} />
        <input data-testid="rename-last-input" className="input-base w-full mb-3" placeholder="Last name (optional)" value={last} onChange={(e) => setLast(e.target.value)} />
        {err && <div className="text-[#FF5100] text-xs mb-2">{err}</div>}
        <div className="flex gap-2">
          <button data-testid="rename-save-btn" type="submit" disabled={busy || !first.trim()} className="btn-primary flex-1 disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

export function PdfModal({ url, title, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" data-testid="pdf-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-3 border-b border-[#E4E4E7]">
          <div className="font-bold font-display text-sm truncate">{title}</div>
          <div className="flex items-center gap-2 shrink-0">
            <a href={url} download={`${(title || "document").replace(/\s+/g, "-").toLowerCase()}.png`}
              className="btn-secondary text-xs leading-none" style={{ padding: "6px 14px" }} data-testid="pdf-download-btn">Download</a>
            <button onClick={onClose} className="btn-ghost" data-testid="pdf-modal-close"><X size={18} weight="bold" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-[#52525B] p-4">
          <img src={url} alt={title} data-testid="pdf-preview-image" className="mx-auto max-w-full h-auto shadow-2xl rounded" />
        </div>
      </div>
    </div>
  );
}

function ClassRow({ c, selected, onClick, showTeacher = false, onQuickPass = null }) {
  const { user } = useAuth();
  const coTeachers = c.co_teachers || [];
  const viewerIsCoTeacher = coTeachers.some((t) => t.id === user?.id);
  const sharedLabel = viewerIsCoTeacher
    ? (c.teacher_name || "the owning teacher") + (coTeachers.length > 1 ? ` +${coTeachers.length - 1} more` : "")
    : (coTeachers[0]?.name || "another teacher") + (coTeachers.length > 1 ? ` +${coTeachers.length - 1} more` : "");
  return (
    <div
      data-testid={`class-item-${c.id}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onClick(); }}
      className="w-full text-left p-3 transition-all block bg-white text-[#000000] cursor-pointer"
      style={{ boxShadow: selected ? "inset 0 0 0 3px #CCFF00" : "none" }}
      aria-pressed={selected}
    >
      <div className="flex items-center gap-2">
        <GraduationCap size={18} weight="duotone" color="#84CC16" className="shrink-0" />
        <span className="font-bold font-display truncate">{c.name}</span>
        {coTeachers.length > 0 && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#ECFCCB] text-[#3F6212] font-bold shrink-0" data-testid={`shared-badge-${c.id}`}>
            shared with {sharedLabel}
          </span>
        )}
        {c.covering_teacher_id && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#DBEAFE] text-[#1E40AF] font-bold shrink-0" data-testid={`covered-badge-${c.id}`}>
            being covered by {c.covering_teacher_name || "another teacher"}
          </span>
        )}
        {onQuickPass && (() => {
          const classPassed = (c.student_count || 0) > 0 && (c.passed_students || 0) >= c.student_count;
          return (
            <button data-testid={`quick-pass-${c.id}`}
              title={classPassed ? "Undo: put every student back on their real, saved progress" : "Pass every student in this class (undo-able)"}
              onClick={(e) => { e.stopPropagation(); onQuickPass(c, classPassed); }}
              className="ml-auto shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border"
              style={classPassed
                ? { background: "#FFF7ED", color: "#9A3412", borderColor: "#FDBA74" }
                : { background: "#EEF7D6", color: "#3F6212", borderColor: "#BEF264" }}>
              {classPassed ? "↺ Undo pass" : "✓ Pass"}
            </button>
          );
        })()}
      </div>
      {showTeacher && (
        <div className="text-xs mt-1 flex items-center gap-1.5 text-[#3F6212]" data-testid={`class-teacher-${c.id}`}>
          <UserSwitch size={13} weight="bold" className="shrink-0" /> {c.teacher_name || "No teacher"}
        </div>
      )}
      <div className="text-xs mt-1 text-[#000000]">
        {c.grade} · {c.course_title || "—"} · {c.student_count} student{c.student_count === 1 ? "" : "s"}
      </div>
      <div className="flex items-center gap-1.5 mt-1.5">
        <div data-testid={`class-progress-bar-${c.id}`} title={`Class average: ${c.avg_progress ?? 0}%`}
          className="flex-1 h-2 rounded-full overflow-hidden"
          style={{ border: "1px solid #0A0D14", background: "#FFFFFF" }}>
          <div className="h-full rounded-full" style={{ width: `${c.avg_progress ?? 0}%`, background: "#CCFF00" }} />
        </div>
        <span data-testid={`class-progress-pct-${c.id}`} className="font-bold text-black shrink-0" style={{ fontSize: "9px", lineHeight: "8px" }}>
          {c.avg_progress ?? 0}%
        </span>
      </div>
    </div>
  );
}

export default function ClassRosterPanel({ courses = [] }) {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  const publishedCourses = courses;

  const loadClasses = async () => {
    setLoading(true); setErr("");
    try { const { data } = await api.get("/classes"); setClasses(data); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const loadTeachers = async () => {
    try { const { data } = await api.get("/classes/teachers"); setTeachers(data); }
    catch (_) { setTeachers([]); }
  };

  const [claimNotices, setClaimNotices] = useState([]);
  const loadNotices = async () => {
    try { const { data } = await api.get("/classes/claim-notices"); setClaimNotices(data); }
    catch (_) { setClaimNotices([]); }
  };
  const undoClaim = async (n) => {
    try {
      await api.post(`/classes/claim-notices/${n.id}/undo`);
      toast.success(`"${n.class_name}" is yours again`);
      loadNotices(); loadClasses();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const dismissNotice = async (n) => {
    try { await api.post(`/classes/claim-notices/${n.id}/dismiss`); setClaimNotices((l) => l.filter((x) => x.id !== n.id)); }
    catch (_) {}
  };

  const loadDetail = async (id) => {
    setSelectedId(id);
    try { const { data } = await api.get(`/classes/${id}`); setDetail(data); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const [searchParams] = useSearchParams();
  useEffect(() => {
    loadClasses(); loadTeachers(); loadNotices();
    // Deep link: /admin?tab=roster&class={id} pre-selects a class (e.g. from the Users table).
    const cid = searchParams.get("class");
    if (cid) loadDetail(cid);
    // eslint-disable-next-line
  }, []);

  const [showReassignAll, setShowReassignAll] = useState(false);

  const quickPassClass = async (c, isUndo = false) => {
    if (isUndo) {
      if (!window.confirm(`Undo the bulk-pass for "${c.name}"? Students go back to their real saved progress.`)) return;
      try {
        const { data } = await api.post(`/classes/${c.id}/restore-progress`);
        toast.success(data.restored ? `"${c.name}": restored ${data.restored} student(s).` : "Nothing to restore — no saved progress found.");
        loadClasses(); if (selectedId === c.id) loadDetail(c.id);
      } catch (e) { toast.error(formatApiError(e)); }
      return;
    }
    if (!window.confirm(`Pass every student in "${c.name}"? Their real progress is saved and can be restored.`)) return;
    try {
      const { data } = await api.post(`/classes/${c.id}/pass-all`);
      toast.success(`"${c.name}": passed ${data.students} student(s) on ${data.passed} lesson(s).`);
      loadClasses(); if (selectedId === c.id) loadDetail(c.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const passSchool = async (schoolName, list) => {
    const sid = list.find((c) => c.school_id)?.school_id;
    if (!sid) { toast.error("These classes aren't linked to a school yet."); return; }
    if (!window.confirm(`Pass EVERY student in EVERY class at ${schoolName} (${list.length} class${list.length === 1 ? "" : "es"})? Each student keeps a restore point.`)) return;
    try {
      const { data } = await api.post("/classes/school-pass-all", { school_id: sid });
      toast.success(`${schoolName}: passed ${data.students} student(s) across ${data.classes} class(es).`);
      loadClasses(); if (selectedId) loadDetail(selectedId);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restoreSchool = async (schoolName, list) => {
    const sid = list.find((c) => c.school_id)?.school_id;
    if (!sid) return;
    if (!window.confirm(`Undo the bulk-pass for the whole of ${schoolName}? Students go back to their real saved progress.`)) return;
    try {
      const { data } = await api.post("/classes/school-restore-all", { school_id: sid });
      toast.success(data.restored ? `${schoolName}: restored ${data.restored} student(s).` : "Nothing to restore — no saved progress found.");
      loadClasses(); if (selectedId) loadDetail(selectedId);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="roster-panel" className="grid md:grid-cols-[340px_1fr] gap-6">
      {claimNotices.length > 0 && (
        <div className="md:col-span-2 space-y-2" data-testid="claim-notices">
          {claimNotices.map((n) => (
            <div key={n.id} data-testid={`claim-notice-${n.id}`}
              className="rounded-2xl border border-[#FDBA74] bg-[#FFF7ED] px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-[#7C2D12] min-w-0">
                <strong>{n.to_teacher_name || "A colleague"}</strong> permanently claimed your class <strong>"{n.class_name}"</strong>.
                <span className="text-xs text-[#9A3412] ml-1">You can undo this for 24 hours.</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid={`claim-undo-${n.id}`} onClick={() => undoClaim(n)} className="btn-primary text-xs" style={{ padding: "5px 14px" }}>
                  Undo — take it back
                </button>
                <button data-testid={`claim-dismiss-${n.id}`} onClick={() => dismissNotice(n)} className="btn-ghost text-xs text-[#9A3412]">Dismiss</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Left: create + list */}
      <div>
        <CreateClassForm courses={publishedCourses} teachers={teachers} onTeachersChanged={loadTeachers}
          onCreated={(c) => { loadClasses(); loadDetail(c.id); }} />
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-[#000000] font-display">{isAdmin ? "Classes by school" : "My classes"}</div>
            {classes.length > 0 && (
              <button data-testid="reassign-all-btn" onClick={() => setShowReassignAll(true)}
                className="btn-ghost text-xs flex items-center gap-1 text-[#000000]" title="Hand all classes off to another teacher">
                <HandArrowDown size={14} weight="bold" /> Hand off all
              </button>
            )}
          </div>
          {loading && <div className="text-[#000000] text-sm">Loading…</div>}
          {!loading && classes.length === 0 && (
            <div className="text-sm text-[#000000] rounded-xl border border-dashed border-[#C4C8CE] p-4">
              No classes yet. Create one above to start adding students.
            </div>
          )}
          {isAdmin ? (
            <div className="space-y-4" data-testid="classes-by-school">
              {Object.entries(
                classes.reduce((acc, c) => {
                  const k = c.school_name || "Unassigned school";
                  (acc[k] = acc[k] || []).push(c);
                  return acc;
                }, {})
              ).sort(([a], [b]) => a.localeCompare(b)).map(([school, list]) => (
                <div key={school} data-testid={`school-group-${school}`}>
                  <div className="flex items-center justify-between gap-2 rounded-t-xl bg-[#0A0D14] px-3 py-2">
                    <span className="text-xs font-display font-bold uppercase tracking-wider truncate" style={{ color: "#CCFF00" }}>{school}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] text-white/70">
                        {list.length} class{list.length === 1 ? "" : "es"} · {list.reduce((s, c) => s + (c.student_count || 0), 0)} students
                      </span>
                      {list.some((c) => c.school_id) && (() => {
                        const totalStudents = list.reduce((s, c) => s + (c.student_count || 0), 0);
                        const passedStudents = list.reduce((s, c) => s + (c.passed_students || 0), 0);
                        const schoolPassed = totalStudents > 0 && passedStudents >= totalStudents;
                        return (
                          <>
                            {schoolPassed ? (
                              <button data-testid={`restore-school-${school}`} onClick={() => restoreSchool(school, list)}
                                title={`Undo: put every student at ${school} back on their real, saved progress`}
                                className="text-[10px] font-bold px-2 py-0.5 rounded-full border cursor-pointer"
                                style={{ background: "#FFF7ED", color: "#9A3412", borderColor: "#FDBA74" }}>
                                ↺ Undo pass school
                              </button>
                            ) : (
                              <button data-testid={`pass-school-${school}`} onClick={() => passSchool(school, list)}
                                title={`Pass every student in every class at ${school} (undo-able)`}
                                className="text-[10px] font-bold px-2 py-0.5 rounded-full border cursor-pointer"
                                style={{ background: "#CCFF00", color: "#000000", borderColor: "#CCFF00" }}>
                                ✓ Pass school
                              </button>
                            )}
                            {!schoolPassed && passedStudents > 0 && (
                              <button data-testid={`restore-school-${school}`} onClick={() => restoreSchool(school, list)}
                                title="Undo a partial bulk-pass for this school"
                                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full border cursor-pointer"
                                style={{ background: "transparent", color: "#FDBA74", borderColor: "#FDBA74" }}>
                                ↺
                              </button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="border border-t-0 border-[#C4C8CE] rounded-b-xl overflow-hidden divide-y divide-[#E4E4E7]">
                    {list.map((c) => <ClassRow key={c.id} c={c} selected={selectedId === c.id} onClick={() => loadDetail(c.id)} showTeacher onQuickPass={quickPassClass} />)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {classes.map((c) => (
                <div key={c.id} className="rounded-xl overflow-hidden" style={{ border: selectedId === c.id ? "3px solid #CCFF00" : "1px solid #C4C8CE" }}>
                  <ClassRow c={c} selected={false} onClick={() => loadDetail(c.id)} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right: detail */}
      <div>
        {err && <div className="text-[#FF5100] mb-3 text-sm" data-testid="roster-error">{err}</div>}
        {!selectedId && (
          <div className="grid place-items-center h-full min-h-[240px] text-center text-[#000000] rounded-2xl border border-dashed border-[#C4C8CE] p-8">
            <div>
              <UsersThree size={40} weight="duotone" color="#84CC16" className="mx-auto mb-2" />
              <p>Select a class to manage its students, or create a new one.</p>
            </div>
          </div>
        )}
        {selectedId && detail && (
          <ClassDetail key={detail.id} detail={detail} teachers={teachers} allClasses={classes} isAdmin={isAdmin} currentUser={user}
            onChanged={() => { loadDetail(selectedId); loadClasses(); }}
            onDeleted={() => { setSelectedId(null); setDetail(null); loadClasses(); }} />
        )}
      </div>

      {showReassignAll && (
        <ReassignAllModal teachers={teachers} isAdmin={isAdmin} currentUser={user} classCount={classes.length}
          onClose={() => setShowReassignAll(false)}
          onDone={() => { setShowReassignAll(false); loadClasses(); }} />
      )}
    </div>
  );
}

function CreateClassForm({ courses, teachers = [], onTeachersChanged, onCreated }) {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [grade, setGrade] = useState("G9");
  const [courseId, setCourseId] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [schools, setSchools] = useState([]);
  const [showNewTeacher, setShowNewTeacher] = useState(false);
  const [nt, setNt] = useState({ first_name: "", last_name: "", email: "", password: "Welcome@123" });
  const [ntBusy, setNtBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const isTeacher = user?.role === "teacher";
  const hasOwnSchool = isTeacher && !!user?.school_id;
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  useEffect(() => {
    if (hasOwnSchool) return; // teacher: class auto-uses their school
    api.get("/schools").then(({ data }) => setSchools(data)).catch(() => {});
  }, [hasOwnSchool]);

  const schoolTeachers = teachers.filter((t) => t.school_id === schoolId);

  const pickSchool = (id) => { setSchoolId(id); setTeacherId(""); setShowNewTeacher(false); };

  const createTeacher = async (e) => {
    e.preventDefault();
    setNtBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/users", {
        first_name: nt.first_name.trim(), last_name: nt.last_name.trim() || null,
        email: nt.email.trim().toLowerCase(), password: nt.password,
        role: "teacher", school_id: schoolId,
      });
      await onTeachersChanged?.();
      setTeacherId(data.id);
      setShowNewTeacher(false);
      setNt({ first_name: "", last_name: "", email: "", password: "Welcome@123" });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setNtBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!hasOwnSchool && !schoolId) { setErr("Pick a school for this class"); return; }
    if (isAdmin && !teacherId) { setErr("Assign a teacher to this class"); return; }
    if (!courseId) { setErr("Pick a course for this class"); return; }
    setBusy(true); setErr("");
    try {
      const body = { name: name.trim(), grade, course_id: courseId };
      if (!hasOwnSchool) body.school_id = schoolId;
      if (isAdmin && teacherId) body.teacher_id = teacherId;
      const { data } = await api.post("/classes", body);
      setName(""); setTeacherId(""); onCreated(data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="create-class-form" className="rounded-2xl border border-[#C4C8CE] bg-white p-4">
      <div className="font-bold font-display mb-3 flex items-center gap-2"><Plus size={16} weight="bold" /> New class</div>
      {!hasOwnSchool && (
        <select data-testid="class-school-select" className="input-base w-full mb-2" value={schoolId} onChange={(e) => pickSchool(e.target.value)} required>
          <option value="">1. Select school…</option>
          {schools.map((s) => <option key={s.id} value={s.id}>{s.name}{s.board_name ? ` — ${s.board_name}` : ""}</option>)}
        </select>
      )}
      {isAdmin && schoolId && (
        <>
          <select data-testid="class-teacher-select" className="input-base w-full mb-2" value={teacherId} onChange={(e) => setTeacherId(e.target.value)} required>
            <option value="">2. Assign a teacher…</option>
            {schoolTeachers.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.email})</option>)}
          </select>
          {!showNewTeacher ? (
            <button type="button" data-testid="new-class-teacher-btn" onClick={() => setShowNewTeacher(true)}
              className="w-full mb-2 rounded-lg border border-dashed border-[#84CC16] text-[#3F6212] bg-[#F7FEE7] hover:bg-[#ECFCCB] text-xs font-semibold py-1.5 transition-colors">
              + Create a new teacher at this school
            </button>
          ) : (
            <div data-testid="new-class-teacher-form" className="rounded-xl border border-[#84CC16] bg-[#F7FEE7] p-2.5 mb-2 flex flex-col gap-2">
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="new-class-teacher-first-name" className="input-base w-full" placeholder="First name" value={nt.first_name} onChange={(e) => setNt({ ...nt, first_name: e.target.value })} />
                <input data-testid="new-class-teacher-last-name" className="input-base w-full" placeholder="Last name" value={nt.last_name} onChange={(e) => setNt({ ...nt, last_name: e.target.value })} />
              </div>
              <input data-testid="new-class-teacher-email" type="email" className="input-base w-full" placeholder="Teacher email" value={nt.email} onChange={(e) => setNt({ ...nt, email: e.target.value })} />
              <input data-testid="new-class-teacher-password" className="input-base w-full" placeholder="Temp password" value={nt.password} onChange={(e) => setNt({ ...nt, password: e.target.value })} />
              <div className="flex gap-2">
                <button type="button" data-testid="new-class-teacher-submit" disabled={ntBusy || !nt.first_name.trim() || !nt.email.trim() || nt.password.length < 6}
                  onClick={createTeacher} className="btn-primary flex-1 text-xs disabled:opacity-50" style={{ padding: "6px 10px" }}>
                  {ntBusy ? "Creating…" : "Create & assign"}
                </button>
                <button type="button" className="btn-ghost text-xs px-2" onClick={() => setShowNewTeacher(false)}>Cancel</button>
              </div>
            </div>
          )}
        </>
      )}
      <input data-testid="class-name-input" className="input-base w-full mb-2" placeholder="Class name (e.g. Grade 9 – Block A)"
        required value={name} onChange={(e) => setName(e.target.value)} />
      <div className="grid grid-cols-2 gap-2 mb-2">
        <select data-testid="class-grade-select" className="input-base" value={grade} onChange={(e) => setGrade(e.target.value)}>
          {GRADES.map((g) => <option key={g} value={g}>{g === "DEMO" ? "Demo (evaluators)" : g}</option>)}
        </select>
        <select data-testid="class-course-select" className="input-base" value={courseId} onChange={(e) => setCourseId(e.target.value)} required>
          <option value="">Select course…</option>
          {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
      </div>
      {err && <div className="text-[#FF5100] text-xs mb-2">{err}</div>}
      <button data-testid="create-class-submit" type="submit" disabled={busy} className="btn-primary w-full">
        {busy ? "Creating…" : "Create class"}
      </button>
    </form>
  );
}

function ClassDetail({ detail, teachers = [], allClasses = [], isAdmin = false, currentUser, onChanged, onDeleted }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [newStudent, setNewStudent] = useState(null); // { name, code, login_token }
  const [showReassign, setShowReassign] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [classPdf, setClassPdf] = useState(null);
  const isCovering = !!detail.covering_teacher_id;

  const addStudent = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/classes/${detail.id}/students`, { first_name: firstName.trim(), last_name: lastName.trim() || null });
      setFirstName(""); setLastName(""); setNewStudent(data); onChanged();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const removeStudent = async (tid) => {
    if (!window.confirm("Remove this student from the class?")) return;
    try { await api.delete(`/classes/${detail.id}/students/${tid}`); onChanged(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const deleteClass = async () => {
    if (!window.confirm("Delete this class and all its students?")) return;
    try { await api.delete(`/classes/${detail.id}`); onDeleted(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const [threshold, setThreshold] = useState(detail.flag_threshold ?? 3);
  useEffect(() => { setThreshold(detail.flag_threshold ?? 3); }, [detail.flag_threshold]);
  const saveThreshold = async (val) => {
    const n = Math.max(1, Math.min(99, parseInt(val, 10) || 3));
    setThreshold(n);
    try { await api.patch(`/classes/${detail.id}`, { flag_threshold: n }); onChanged(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const printAllCards = async () => {
    try {
      const { data } = await api.get(`/classes/${detail.id}/id-cards?format=png`, { responseType: "blob" });
      setClassPdf({ url: URL.createObjectURL(new Blob([data], { type: "image/png" })), title: `${detail.name} — ID cards` });
    } catch (e) { setErr(formatApiError(e)); }
  };

  const takeOver = async () => {
    try { await api.post(`/classes/${detail.id}/take-over`); onChanged(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const release = async () => {
    try { await api.post(`/classes/${detail.id}/release`); onChanged(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const unshare = async (t) => {
    if (!window.confirm(`Stop sharing this class with ${t.name || "this co-teacher"}?`)) return;
    try {
      await api.post(`/classes/${detail.id}/unshare`, { teacher_id: t.id });
      toast.success(`${t.name || "Co-teacher"} removed from this class`);
      onChanged();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const passAllClass = async () => {
    setErr("");
    try {
      const { data } = await api.post(`/classes/${detail.id}/pass-all`);
      toast.success(`Passed ${data.students} student(s) on ${data.passed} lesson(s). Use "Undo pass all" to put them back.`);
      onChanged();
    } catch (e) { setErr(formatApiError(e)); toast.error("Couldn't pass the class — " + formatApiError(e)); }
  };
  const restoreAllClass = async () => {
    setErr("");
    try {
      const { data } = await api.post(`/classes/${detail.id}/restore-progress`);
      toast.success(`Restored ${data.restored} student(s) to their real progress`);
      onChanged();
    } catch (e) { setErr(formatApiError(e)); toast.error("Couldn't restore — " + formatApiError(e)); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid="class-detail" className="rounded-2xl border border-[#C4C8CE] bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-extrabold font-display">{detail.name}</h2>
          <p className="text-sm text-[#000000]">
            <span className="chip font-bold" style={{ background: "#CCFF00", color: "#000000", padding: "1px 8px", fontSize: "0.7rem" }}>{detail.grade}</span>
            <span className="ml-2">{detail.course_title}</span>
          </p>
          {(detail.school_name || detail.board_name) && (
            <p className="text-xs text-[#000000] mt-1">{detail.school_name}{detail.board_name ? ` · ${detail.board_name}` : ""}</p>
          )}
          <p className="text-xs text-[#000000] mt-1">Every student here is {detail.grade} and sees only {detail.grade}-flagged lessons. Students log in with their first name + ID.</p>
        </div>
        <button data-testid="delete-class-btn" onClick={deleteClass} className="btn-ghost text-[#FF5100]"><Trash size={18} weight="bold" /></button>
      </div>

      <div className="flex flex-wrap items-center justify-between w-full gap-2 mt-3 pt-3 border-t border-[#F1F1F3]">
        <button data-testid="print-all-cards-btn" onClick={printAllCards} className="btn-secondary text-xs flex items-center gap-1.5 leading-tight text-left" style={{ padding: "5px 10px" }}>
          <IdentificationCard size={16} weight="bold" className="shrink-0" /> <span>Print all<br />ID cards</span>
        </button>
        <label className="flex items-center gap-1.5 text-sm font-bold text-[#000000] whitespace-nowrap" title="Flag a student once they mark this many lessons 'come back later'">
          <Flag size={16} weight="fill" color="#FF5100" className="shrink-0" /> Flag after
          <input data-testid="flag-threshold-input" type="number" min={1} max={99} value={threshold}
            onChange={(e) => setThreshold(e.target.value)} onBlur={(e) => saveThreshold(e.target.value)}
            className="input-base w-10 text-center" style={{ padding: "3px 2px" }} />
          come-back-laters
        </label>
        <button data-testid="reassign-class-btn" onClick={() => setShowReassign(true)} className="btn-secondary text-xs flex items-center gap-1.5 leading-tight text-left" style={{ padding: "5px 10px" }}>
          <ArrowsLeftRight size={16} weight="bold" className="shrink-0" /> <span>Reassign<br />class</span>
        </button>
        <button data-testid="share-class-btn" onClick={() => setShowShare(true)} className="btn-secondary text-xs flex items-center gap-1.5 leading-tight text-left" style={{ padding: "5px 10px" }}
          title="Add a co-teacher: they get full, equal access to this class">
          <UsersThree size={16} weight="bold" className="shrink-0" /> <span>Share<br />class</span>
        </button>
        <button data-testid="pass-all-class-btn" onClick={passAllClass} className="btn-secondary text-xs flex items-center gap-1.5 leading-tight text-left" style={{ padding: "5px 10px", background: "#EEF7D6", color: "#3F6212", borderColor: "#BEF264" }}
          title="Mark every lesson completed for every student (their real progress is saved for undo)">
          <CheckCircle size={16} weight="bold" className="shrink-0" /> <span>Pass all<br />students</span>
        </button>
        {(detail.students || []).some((s) => s.has_backup) && (
          <button data-testid="restore-all-class-btn" onClick={restoreAllClass} className="btn-secondary text-xs flex items-center gap-1.5 leading-tight text-left" style={{ padding: "5px 10px", background: "#FFF7ED", color: "#9A3412", borderColor: "#FDBA74" }}
            title="Undo 'Pass all students': put every student back on their real, saved progress">
            <ArrowCounterClockwise size={16} weight="bold" className="shrink-0" /> <span>Undo pass<br />all students</span>
          </button>
        )}
        {isCovering && (isAdmin || detail.covering_teacher_id === currentUser?.id || detail.teacher_id === currentUser?.id) && (
          <button data-testid="release-class-btn" onClick={release} className="btn-ghost text-sm flex items-center gap-1.5 text-[#1E40AF] border border-[#BFDBFE] rounded-xl">
            <HandArrowDown size={16} weight="bold" />
            {detail.teacher_id === currentUser?.id && detail.covering_teacher_id !== currentUser?.id ? "Take back control" : "Release cover"}
          </button>
        )}
        {isAdmin && !isCovering && (
          <button data-testid="take-over-class-btn" onClick={takeOver} className="btn-ghost text-sm flex items-center gap-1.5 text-[#000000] border border-[#C4C8CE] rounded-xl">
            <HandArrowDown size={16} weight="bold" /> Take over
          </button>
        )}
      </div>

      {(detail.co_teachers || []).length > 0 && (
        <div data-testid="shared-class-notice" className="mt-3 text-xs rounded-lg bg-[#F7FEE7] border border-[#BEF264] text-[#3F6212] px-3 py-2 space-y-1.5">
          <span className="flex items-center gap-1.5">
            <UsersThree size={15} weight="bold" className="shrink-0" />
            {(detail.co_teachers || []).some((t) => t.id === currentUser?.id)
              ? <>You co-teach this class with <strong>{detail.teacher_name || "the owning teacher"}</strong>{detail.co_teachers.length > 1 ? <> and {detail.co_teachers.length - 1} other co-teacher{detail.co_teachers.length > 2 ? "s" : ""}</> : null} — everyone has full access.</>
              : <>Shared — all teachers below have full, equal access.</>}
          </span>
          <div className="flex flex-wrap gap-1.5">
            {(detail.co_teachers || []).map((t) => (
              <span key={t.id} data-testid={`co-teacher-chip-${t.id}`} className="inline-flex items-center gap-1 bg-white border border-[#BEF264] rounded-full px-2 py-0.5">
                <strong>{t.id === currentUser?.id ? "You" : (t.name || "another teacher")}</strong>
                <button data-testid={`unshare-class-btn-${t.id}`} onClick={() => unshare(t)}
                  title={`Stop sharing with ${t.name || "this co-teacher"}`}
                  className="btn-ghost text-[#3F6212] leading-none p-0 ml-0.5">
                  <X size={12} weight="bold" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {isCovering && (
        <div data-testid="covering-notice" className="mt-3 text-xs rounded-lg bg-[#EFF6FF] border border-[#BFDBFE] text-[#1E40AF] px-3 py-2">
          {detail.covering_teacher_id === currentUser?.id
            ? "You are covering this class. Release cover to hand control back to the owning teacher."
            : detail.teacher_id === currentUser?.id
              ? `This class is being covered by ${detail.covering_teacher_name || "another teacher"}. Use "Take back control" to end the cover.`
              : `This class is being covered by ${detail.covering_teacher_name || "another teacher"}.`}
        </div>
      )}

      {detail.seat_summary && (
        <div data-testid="seat-summary-strip" className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-[#000000] font-display mr-1">Seats</span>
          <span data-testid="seat-count-active" className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#DCFCE7] text-[#166534] flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#16A34A]" /> {detail.seat_summary.active || 0} active
          </span>
          <span data-testid="seat-count-grace" className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#FEF3C7] text-[#92400E] flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#F59E0B]" /> {detail.seat_summary.grace || 0} in grace
          </span>
          <span data-testid="seat-count-expired" className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#FEE2E2] text-[#B91C1C] flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#DC2626]" /> {detail.seat_summary.expired || 0} expired
          </span>
        </div>
      )}

      {err && <div className="text-[#FF5100] text-sm mt-3">{err}</div>}

      <form onSubmit={addStudent} data-testid="add-student-form" className="flex gap-2 mt-4 flex-wrap">
        <input data-testid="student-name-input" className="input-base flex-1 min-w-[140px]" placeholder="First name" required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        <input data-testid="student-lastname-input" className="input-base flex-1 min-w-[140px]" placeholder="Last name (optional)" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        <button data-testid="add-student-submit" type="submit" disabled={busy} className="btn-primary whitespace-nowrap"><Plus size={16} weight="bold" /> Add</button>
      </form>
      <p className="text-[11px] text-[#000000] mt-1">Students still log in with just their first name + ID — the last name only appears on rosters, ID cards & certificates.</p>

      {newStudent && <QuickLoginCard student={newStudent} onClose={() => setNewStudent(null)} highlight />}

      <div className="mt-5">
        <div className="text-xs uppercase tracking-wider text-[#000000] mb-2 font-display">Students ({detail.students?.length || 0})</div>
        {(!detail.students || detail.students.length === 0) && (
          <div className="text-sm text-[#000000]">No students yet. Add one above — they'll get a quick-login link.</div>
        )}
        <div className="space-y-2">
          {(detail.students || []).map((s) => (
            <StudentRow key={s.id} classId={detail.id} student={s} allClasses={allClasses}
              isSuperAdmin={currentUser?.role === "super_admin"}
              onRemove={() => removeStudent(s.id)} onChanged={onChanged} />
          ))}
        </div>
      </div>

      {showReassign && (
        <ReassignClassModal classId={detail.id} teachers={teachers} currentTeacherId={detail.teacher_id}
          onClose={() => setShowReassign(false)}
          onDone={() => { setShowReassign(false); onChanged(); }} />
      )}
      {showShare && (
        <ShareClassModal classId={detail.id} teachers={teachers}
          excludeIds={[detail.teacher_id, ...(detail.co_teachers || []).map((t) => t.id)].filter(Boolean)}
          onClose={() => setShowShare(false)}
          onDone={(name) => { setShowShare(false); toast.success(`Class shared with ${name}`); onChanged(); }} />
      )}
      {classPdf && <PdfModal url={classPdf.url} title={classPdf.title} onClose={() => { URL.revokeObjectURL(classPdf.url); setClassPdf(null); }} />}
    </motion.div>
  );
}

function StudentRow({ classId, student, allClasses = [], isSuperAdmin = false, onRemove, onChanged }) {
  const [token, setToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [showMigrate, setShowMigrate] = useState(false);
  const [showGrace, setShowGrace] = useState(false);
  const [rowErr, setRowErr] = useState("");
  const [pdf, setPdf] = useState(null);
  const [showRename, setShowRename] = useState(false);

  const getLink = async () => {
    if (token) return token;
    setBusy(true);
    try {
      const { data } = await api.post(`/classes/${classId}/students/${student.id}/login-link`);
      setToken(data.login_token);
      return data.login_token;
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    const t = await getLink();
    try { await navigator.clipboard.writeText(loginUrl(t)); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch (_) {}
  };
  const openAs = async () => { const t = await getLink(); window.open(loginUrl(t), "_blank", "noopener"); };

  const openIdCard = async () => {
    try {
      const { data } = await api.get(`/classes/${classId}/students/${student.id}/id-card?format=png`, { responseType: "blob" });
      setPdf({ url: URL.createObjectURL(new Blob([data], { type: "image/png" })), title: `${student.name} — ID card` });
    } catch (_) {}
  };

  const regenerate = async () => {
    if (!window.confirm(`Issue a new ID for ${student.name}? Their old ID stops working; progress is kept.`)) return;
    try { await api.post(`/classes/${classId}/students/${student.id}/regenerate-id`); onChanged && onChanged(); } catch (_) {}
  };

  const downloadCert = async () => {
    setRowErr("");
    try {
      const { data } = await api.get(`/classes/${classId}/students/${student.id}/certificate?format=png`, { responseType: "blob" });
      setPdf({ url: URL.createObjectURL(new Blob([data], { type: "image/png" })), title: `${student.name} — Certificate` });
    } catch (e) {
      // Blob error responses need to be parsed back to text for the message.
      let msg = "Couldn't issue certificate.";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) {}
      setRowErr(msg);
    }
  };

  return (
    <div data-testid={`student-row-wrap-${student.id}`}>
    <div data-testid={`student-row-${student.id}`} className="flex items-center justify-between gap-2 rounded-xl border p-2.5" style={{ borderColor: student.flagged ? "#FCA5A5" : "#E4E4E7", background: student.flagged ? "#FEF2F2" : "#fff" }}>
      <div className="min-w-0">
        <div className="font-semibold truncate flex items-center gap-2">
          {student.name}
          <button data-testid={`rename-${student.id}`} onClick={() => setShowRename(true)} title="Edit student name" className="text-[#000000] hover:text-[#000000] transition-colors shrink-0"><PencilSimple size={13} weight="bold" /></button>
          {student.flagged && (
            <span data-testid={`flag-badge-${student.id}`} title={`${student.skipped_count} come-back-laters: ${(student.skipped_titles || []).join(", ")}`} className="text-[10px] px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#B91C1C] flex items-center gap-1 font-bold">
              <Flag size={11} weight="fill" /> {student.skipped_count}
            </span>
          )}
          {student.skipped_count > 0 && !student.flagged && (
            <span data-testid={`skipped-badge-${student.id}`} title={`Come back later: ${(student.skipped_titles || []).join(", ")}`} className="text-[10px] px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E] flex items-center gap-1 font-bold">
              <Flag size={11} weight="bold" /> {student.skipped_count} skipped
            </span>
          )}
          {student.activated
            ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#DCFCE7] text-[#166534]">active</span>
            : <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E]">new</span>}
          {student.seat_state === "grace" && (
            <span data-testid={`seat-badge-grace-${student.id}`} title={`Access until ${student.grace_until}`} className="text-[10px] px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E] font-bold">in grace</span>
          )}
          {student.seat_state === "expired" && (
            <span data-testid={`seat-badge-expired-${student.id}`} title={`Expired ${student.expires_on}`} className="text-[10px] px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#B91C1C] font-bold">expired</span>
          )}
        </div>
        <div className="text-xs text-[#000000] flex items-center gap-1 flex-wrap">
          <span>ID <span className="font-mono font-bold text-[#000000]">{student.student_id}</span> ·</span>
          {(student.progress_percent ?? 0) >= 100 ? (
            <span className="flex items-center gap-1 font-bold text-[#000000]" data-testid={`student-passed-${student.id}`}>
              <CheckCircle size={14} weight="fill" color="#16A34A" /> Passed
            </span>
          ) : (
            <span>{student.progress_percent ?? 0}% complete</span>
          )}
        </div>
        {student.skipped_count > 0 && (
          <div className="text-xs text-[#B45309] mt-0.5 truncate" data-testid={`skipped-lessons-${student.id}`}>
            Come back later: {(student.skipped_titles || []).join(" · ")}
          </div>
        )}
      </div>
      <div className="shrink-0 flex flex-col items-stretch gap-1">
        <div className="flex items-center gap-1 flex-wrap justify-end">
          <button data-testid={`progress-${student.id}`} onClick={() => setShowProgress(true)} title="Manage lesson status" className="btn-ghost text-xs flex items-center gap-1"><ListChecks size={15} weight="bold" /> Progress</button>
          <button data-testid={`migrate-${student.id}`} onClick={() => setShowMigrate(true)} title="Move to another class (keeps ID + progress)" className="btn-ghost text-xs flex items-center gap-1"><UserSwitch size={15} weight="bold" /> Move</button>
          <button data-testid={`cert-${student.id}`} onClick={downloadCert} title="Re-issue completion certificate" className="btn-ghost text-xs flex items-center gap-1"><Certificate size={15} weight="bold" /> Cert</button>
          {isSuperAdmin && (
            <button data-testid={`grace-${student.id}`} onClick={() => setShowGrace(true)} title="Extend grace period" className="btn-ghost text-xs flex items-center gap-1"><Clock size={15} weight="bold" /> Grace</button>
          )}
          <button data-testid={`id-card-${student.id}`} onClick={openIdCard} title="Print ID card" className="btn-ghost text-xs flex items-center gap-1"><IdentificationCard size={15} weight="bold" /> ID Card</button>
          <button data-testid={`regen-id-${student.id}`} onClick={regenerate} title="Issue new ID" className="btn-ghost text-xs"><ArrowsClockwise size={14} weight="bold" /></button>
          <button data-testid={`copy-login-${student.id}`} onClick={copyLink} disabled={busy} title="Copy teacher preview link" className="btn-ghost text-xs">
            {copied ? <Check size={14} weight="bold" /> : <Copy size={14} />}
          </button>
          <button data-testid={`open-as-${student.id}`} onClick={openAs} disabled={busy} title="Preview as this student" className="btn-ghost text-xs"><ArrowSquareOut size={14} weight="bold" /></button>
          <button data-testid={`remove-student-${student.id}`} onClick={onRemove} title="Remove student" className="btn-ghost text-[#FF5100]"><Trash size={15} /></button>
        </div>
        <div className="flex items-center gap-1.5 w-full">
          <div data-testid={`progress-bar-${student.id}`} title={`${student.progress_percent ?? 0}% complete`}
            className="flex-1 h-2 rounded-full overflow-hidden"
            style={{ border: "1px solid #0A0D14", background: "#FFFFFF" }}>
            <div className="h-full rounded-full" style={{ width: `${student.progress_percent ?? 0}%`, background: "#CCFF00" }} />
          </div>
          <span data-testid={`progress-pct-${student.id}`} className="font-bold text-black shrink-0" style={{ fontSize: "9px", lineHeight: "8px" }}>
            {student.progress_percent ?? 0}%
          </span>
        </div>
      </div>
    </div>
    {rowErr && <div data-testid={`student-row-error-${student.id}`} className="text-[#FF5100] text-xs mt-1 px-1">{rowErr}</div>}
    {showProgress && <ProgressOverride classId={classId} student={student} onClose={() => setShowProgress(false)} onChanged={onChanged} />}
    {pdf && <PdfModal url={pdf.url} title={pdf.title} onClose={() => { URL.revokeObjectURL(pdf.url); setPdf(null); }} />}
    {showRename && <RenameStudentModal classId={classId} student={student} onClose={() => setShowRename(false)} onSaved={() => { setShowRename(false); onChanged(); }} />}
    {showMigrate && <MigrateStudentModal classId={classId} student={student} allClasses={allClasses} onClose={() => setShowMigrate(false)} onDone={() => { setShowMigrate(false); onChanged(); }} />}
    {showGrace && <GraceModal classId={classId} student={student} onClose={() => setShowGrace(false)} onDone={() => { setShowGrace(false); onChanged(); }} />}
    </div>
  );
}

function ProgressOverride({ classId, student, onClose, onChanged }) {
  const [lessons, setLessons] = useState(null);
  const [hasBackup, setHasBackup] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/classes/${classId}/students/${student.id}/progress`);
      setLessons(data.lessons); setHasBackup(!!data.has_backup);
    }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (lid, status) => {
    setLessons((ls) => ls.map((l) => l.lesson_asset_id === lid ? { ...l, status } : l));
    try { await api.post(`/classes/${classId}/students/${student.id}/lesson-status`, { lesson_asset_id: lid, status }); onChanged && onChanged(); }
    catch (e) { setErr(formatApiError(e)); load(); }
  };

  const passAll = async () => {
    setErr("");
    try {
      const { data } = await api.post(`/classes/${classId}/students/${student.id}/pass-all`);
      toast.success(`${student.name} marked completed on ${data.passed} lesson(s)`);
      await load(); onChanged && onChanged();
    } catch (e) { setErr(formatApiError(e)); }
  };
  const restore = async () => {
    setErr("");
    try {
      await api.post(`/classes/${classId}/students/${student.id}/restore-progress`);
      toast.success(`${student.name} restored to their real progress`);
      await load(); onChanged && onChanged();
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="progress-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#E4E4E7]">
          <div className="font-bold font-display">{student.name} · lesson status</div>
          <button data-testid="progress-modal-close" onClick={onClose} className="btn-ghost"><X size={18} weight="bold" /></button>
        </div>
        <div className="flex items-center gap-2 px-4 pt-3">
          <button data-testid="pass-all-student-btn" onClick={passAll}
            className="btn-secondary text-xs flex items-center gap-1.5" style={{ padding: "5px 12px", background: "#EEF7D6", color: "#3F6212", borderColor: "#BEF264" }}
            title="Mark every lesson completed (real progress is saved for undo)">
            <CheckCircle size={14} weight="bold" /> Pass all lessons
          </button>
          {hasBackup && (
            <button data-testid="restore-student-btn" onClick={restore}
              className="btn-ghost text-xs flex items-center gap-1.5 text-[#1E40AF] border border-[#BFDBFE] rounded-xl" style={{ padding: "5px 12px" }}
              title="Undo: back to their real progress">
              <ArrowCounterClockwise size={14} weight="bold" /> Restore real progress
            </button>
          )}
        </div>
        {err && <div className="text-[#FF5100] text-sm px-4 pt-2">{err}</div>}
        <div className="overflow-y-auto p-4 space-y-2">
          {!lessons && <div className="text-[#000000] text-sm">Loading…</div>}
          {lessons && lessons.length === 0 && <div className="text-[#000000] text-sm">No lessons for this grade.</div>}
          {(lessons || []).map((l) => (
            <div key={l.lesson_asset_id} data-testid={`progress-lesson-${l.lesson_asset_id}`} className="flex items-center justify-between gap-2 rounded-lg border border-[#E4E4E7] px-3 py-2">
              <span className="text-sm truncate flex-1">{l.title}</span>
              <select data-testid={`progress-status-${l.lesson_asset_id}`} className="input-base text-xs shrink-0" value={l.status} onChange={(e) => setStatus(l.lesson_asset_id, e.target.value)} style={{ padding: "4px 8px", width: "auto" }}>
                <option value="incomplete">Incomplete</option>
                <option value="skipped">Skipped</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ModalShell({ title, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid={testId} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#E4E4E7]">
          <div className="font-bold font-display">{title}</div>
          <button data-testid="modal-close" onClick={onClose} className="btn-ghost"><X size={18} weight="bold" /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function ShareClassModal({ classId, teachers, excludeIds = [], onClose, onDone }) {
  const [teacherId, setTeacherId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const options = teachers.filter((t) => !excludeIds.includes(t.id));
  const submit = async () => {
    if (!teacherId) { setErr("Pick a teacher"); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/classes/${classId}/share`, { teacher_id: teacherId });
      onDone(data.co_teacher_name || "the co-teacher");
    } catch (e) { setErr(formatApiError(e)); setBusy(false); }
  };
  return (
    <ModalShell title="Share this class" onClose={onClose} testId="share-class-modal">
      <p className="text-sm text-[#000000] mb-3">Add a co-teacher. Both of you keep full, equal access — the roster, progress and pass controls are shared. You can stop sharing any time.</p>
      <select data-testid="share-teacher-select" className="input-base w-full mb-3" value={teacherId} onChange={(e) => setTeacherId(e.target.value)}>
        <option value="">Select a co-teacher…</option>
        {options.map((t) => <option key={t.id} value={t.id}>{t.name || t.email}</option>)}
      </select>
      {err && <div className="text-[#FF5100] text-sm mb-2">{err}</div>}
      <button data-testid="share-class-confirm" onClick={submit} disabled={busy} className="btn-primary w-full">
        {busy ? "Sharing…" : "Share class"}
      </button>
    </ModalShell>
  );
}

function ReassignClassModal({ classId, teachers, currentTeacherId, onClose, onDone }) {
  const [teacherId, setTeacherId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const options = teachers.filter((t) => t.id !== currentTeacherId);
  const submit = async () => {
    if (!teacherId) { setErr("Pick a teacher"); return; }
    setBusy(true); setErr("");
    try { await api.post(`/classes/${classId}/reassign`, { teacher_id: teacherId }); onDone(); }
    catch (e) { setErr(formatApiError(e)); setBusy(false); }
  };
  return (
    <ModalShell title="Reassign this class" onClose={onClose} testId="reassign-class-modal">
      <p className="text-sm text-[#000000] mb-3">Hand this class and its whole roster to another teacher.</p>
      <select data-testid="reassign-teacher-select" className="input-base w-full mb-3" value={teacherId} onChange={(e) => setTeacherId(e.target.value)}>
        <option value="">Select a teacher…</option>
        {options.map((t) => <option key={t.id} value={t.id}>{t.name || t.email}</option>)}
      </select>
      {err && <div className="text-[#FF5100] text-sm mb-2">{err}</div>}
      <button data-testid="reassign-class-confirm" onClick={submit} disabled={busy} className="btn-primary w-full">
        {busy ? "Reassigning…" : "Reassign class"}
      </button>
    </ModalShell>
  );
}

function ReassignAllModal({ teachers, isAdmin, currentUser, classCount = 0, onClose, onDone }) {
  const [fromId, setFromId] = useState(isAdmin ? "" : currentUser?.id || "");
  const [toId, setToId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    if (!fromId) { setErr("Pick the teacher to hand off from"); return; }
    if (!toId) { setErr("Pick the teacher to hand off to"); return; }
    if (fromId === toId) { setErr("Pick two different teachers"); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/classes/reassign-all", { from_teacher_id: fromId, to_teacher_id: toId });
      if (data.moved === 0) setErr("That teacher has no classes to hand off.");
      else onDone();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Hand off all classes" onClose={onClose} testId="reassign-all-modal">
      <p className="text-sm text-[#000000] mb-3">Move every class from one teacher to another in one step. Rosters and progress stay intact.</p>
      {!isAdmin && (
        <div data-testid="reassign-all-count" className="text-sm rounded-lg bg-[#FFFBEB] border border-[#FDE68A] text-[#92400E] px-3 py-2 mb-3">
          This will move <strong>all {classCount} of your class{classCount === 1 ? "" : "es"}</strong> to the teacher you pick. You'll no longer own {classCount === 1 ? "it" : "them"}.
        </div>
      )}
      {isAdmin && (
        <>
          <label className="text-xs uppercase tracking-wider text-[#000000]">From teacher</label>
          <select data-testid="reassign-all-from-select" className="input-base w-full mb-3 mt-1" value={fromId} onChange={(e) => setFromId(e.target.value)}>
            <option value="">Select a teacher…</option>
            {teachers.map((t) => <option key={t.id} value={t.id}>{t.name || t.email}</option>)}
          </select>
        </>
      )}
      <label className="text-xs uppercase tracking-wider text-[#000000]">To teacher</label>
      <select data-testid="reassign-all-to-select" className="input-base w-full mb-3 mt-1" value={toId} onChange={(e) => setToId(e.target.value)}>
        <option value="">Select a teacher…</option>
        {teachers.filter((t) => t.id !== fromId).map((t) => <option key={t.id} value={t.id}>{t.name || t.email}</option>)}
      </select>
      {err && <div className="text-[#FF5100] text-sm mb-2">{err}</div>}
      <button data-testid="reassign-all-confirm" onClick={submit} disabled={busy} className="btn-primary w-full">
        {busy ? "Handing off…" : "Hand off all classes"}
      </button>
    </ModalShell>
  );
}

function MigrateStudentModal({ classId, student, allClasses, onClose, onDone }) {
  const [targetId, setTargetId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const options = (allClasses || []).filter((c) => c.id !== classId);
  const submit = async () => {
    if (!targetId) { setErr("Pick the class to move into"); return; }
    setBusy(true); setErr("");
    try { await api.post(`/classes/${classId}/students/${student.id}/migrate`, { target_class_id: targetId }); onDone(); }
    catch (e) { setErr(formatApiError(e)); setBusy(false); }
  };
  return (
    <ModalShell title={`Move ${student.name}`} onClose={onClose} testId="migrate-student-modal">
      <p className="text-sm text-[#000000] mb-3">
        Moving a student keeps their <strong>ID ({student.student_id})</strong>, XP, streaks, and all completed lessons. They simply continue in the new class — no starting over.
      </p>
      <select data-testid="migrate-target-select" className="input-base w-full mb-3" value={targetId} onChange={(e) => setTargetId(e.target.value)}>
        <option value="">Select destination class…</option>
        {options.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.grade}{c.course_title ? ` · ${c.course_title}` : ""}</option>)}
      </select>
      {options.length === 0 && <div className="text-xs text-[#000000] mb-2">No other classes available to move into.</div>}
      {err && <div className="text-[#FF5100] text-sm mb-2">{err}</div>}
      <button data-testid="migrate-confirm" onClick={submit} disabled={busy || options.length === 0} className="btn-primary w-full">
        {busy ? "Moving…" : "Move student"}
      </button>
    </ModalShell>
  );
}

function GraceModal({ classId, student, onClose, onDone }) {
  const [graceUntil, setGraceUntil] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    if (!graceUntil) { setErr("Pick a date"); return; }
    if (!reason.trim()) { setErr("A reason is required"); return; }
    setBusy(true); setErr("");
    try { await api.post(`/classes/${classId}/students/${student.id}/grace`, { grace_until: graceUntil, reason: reason.trim() }); onDone(); }
    catch (e) { setErr(formatApiError(e)); setBusy(false); }
  };
  return (
    <ModalShell title={`Extend grace — ${student.name}`} onClose={onClose} testId="grace-modal">
      <p className="text-sm text-[#000000] mb-3">Give this student access through a specific date, regardless of their seat expiry. Audit-logged.</p>
      <label className="text-xs uppercase tracking-wider text-[#000000]">Access until</label>
      <input data-testid="grace-date-input" type="date" className="input-base w-full mb-3 mt-1" value={graceUntil} onChange={(e) => setGraceUntil(e.target.value)} />
      <label className="text-xs uppercase tracking-wider text-[#000000]">Reason</label>
      <textarea data-testid="grace-reason-input" className="input-base w-full mb-3 mt-1" rows={2} placeholder="e.g. Board budget approval delayed" value={reason} onChange={(e) => setReason(e.target.value)} />
      {err && <div className="text-[#FF5100] text-sm mb-2">{err}</div>}
      <button data-testid="grace-confirm" onClick={submit} disabled={busy} className="btn-primary w-full">
        {busy ? "Saving…" : "Extend grace"}
      </button>
    </ModalShell>
  );
}

function QuickLoginCard({ student, onClose, highlight }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(student.student_id); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch (_) {} };
  return (
    <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} data-testid="new-student-card"
      className="mt-3 rounded-xl border p-3" style={{ borderColor: highlight ? "#84CC16" : "#E4E4E7", background: "#F7FEE7" }}>
      <div className="font-bold font-display text-[#3F6212]">{student.name} added</div>
      <div className="text-xs text-[#000000] mt-1">They log in with their <strong>first name + this ID</strong>:</div>
      <div className="flex items-center gap-2 mt-2">
        <span data-testid="new-student-id" className="font-mono font-bold text-2xl tracking-[0.25em] text-[#000000]">{student.student_id}</span>
        <button data-testid="new-student-copy" onClick={copy} className="btn-primary text-xs whitespace-nowrap">{copied ? "Copied" : "Copy ID"}</button>
      </div>
      <div className="text-[11px] text-[#000000] mt-2">Print the ID card from the student's row to hand out.</div>
      <button onClick={onClose} className="btn-ghost text-xs mt-1 text-[#000000]">Dismiss</button>
    </motion.div>
  );
}
